import React from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Loader2, AlertCircle, CheckCircle2, ShieldCheck } from "lucide-react";
import apiClient, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

/**
 * v23.24 — Managed-driver invite acceptance page.
 * Reached from the magic link `/managed-invite/accept?token=...` in the invite
 * email. Verifies the token, then lets the invitee choose a password and land
 * straight on the managed driver dashboard.
 */
export default function ManagedInviteAcceptPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const token = params.get("token") || "";

  const [loading, setLoading] = React.useState(true);
  const [invite, setInvite] = React.useState(null);
  const [error, setError] = React.useState("");

  const [name, setName] = React.useState("");
  const [phone, setPhone] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [confirm, setConfirm] = React.useState("");
  const [submitting, setSubmitting] = React.useState(false);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!token) { setLoading(false); return; }
      try {
        const { data } = await apiClient.get(`/managed-invite/verify?token=${encodeURIComponent(token)}`);
        if (cancelled) return;
        setInvite(data);
        setName(data.name || "");
      } catch (e) {
        if (!cancelled) setError(formatApiErrorDetail(e.response?.data?.detail) || "This invite link is no longer valid.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!name.trim()) { setError("Please enter your full name."); return; }
    if (password.length < 8) { setError("Password must be at least 8 characters."); return; }
    if (password !== confirm) { setError("Passwords don't match."); return; }
    setSubmitting(true);
    try {
      await apiClient.post("/managed-invite/accept", {
        token,
        name: name.trim(),
        phone: phone.trim() || null,
        password,
      });
      await refresh();
      navigate("/dashboard/driver", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't accept this invite. Please ask your admin to resend it.");
    } finally { setSubmitting(false); }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-[#FAFAF7] flex items-center justify-center p-6">
        <div className="w-full max-w-md bg-white border border-[#0A0A0A]/10 p-10 text-center" data-testid="invite-missing-token">
          <AlertCircle size={32} className="text-[#B91C1C] mx-auto" />
          <h1 className="mt-4 font-display text-2xl font-black tracking-tight text-[#0A0A0A]">Missing invite token</h1>
          <p className="mt-3 text-sm text-[#475569]">This link looks incomplete. Ask your admin to resend the invite email.</p>
          <Link to="/login" className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-[#1E4D55]">
            Back to sign in
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAF7] flex items-center justify-center p-6">
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="w-full max-w-md"
      >
        <Link to="/login" data-testid="invite-back-to-login" className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.18em] font-bold text-[#475569] hover:text-[#0A0A0A] mb-8">
          <ArrowLeft size={12} /> Back to sign in
        </Link>

        <div className="bg-white border border-[#0A0A0A]/10 p-10" data-testid="managed-invite-card">
          {loading ? (
            <div className="text-sm text-[#475569] inline-flex items-center gap-2" data-testid="invite-loading">
              <Loader2 size={16} className="animate-spin" /> Checking your invite&hellip;
            </div>
          ) : error && !invite ? (
            <>
              <AlertCircle size={28} className="text-[#B91C1C]" />
              <h1 className="mt-3 font-display text-2xl font-black tracking-tight text-[#0A0A0A]">Invite unavailable</h1>
              <p className="mt-3 text-sm text-[#475569]" data-testid="invite-error">{error}</p>
              <Link to="/login" className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-[#1E4D55]">Back to sign in</Link>
            </>
          ) : (
            <>
              <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] font-bold text-[#1E4D55]">
                <ShieldCheck size={14} /> Raanah team invite
              </div>
              <h1 className="mt-3 font-display text-3xl font-black tracking-tight text-[#0A0A0A]">
                Welcome to Raanah.
              </h1>
              <p className="mt-3 text-sm text-[#475569] leading-relaxed">
                We&apos;ve set up a managed driver account for{" "}
                <strong className="text-[#0A0A0A]" data-testid="invite-email">{invite.email}</strong>.
                Pick a password to activate it — you&apos;ll get straight access to your Raanah ID card and shift clocking tool.
              </p>

              {error && (
                <div className="mt-5 flex items-start gap-2 text-sm text-[#B91C1C] bg-[#FEE2E2] border border-[#FCA5A5] p-3" data-testid="invite-form-error">
                  <AlertCircle size={16} className="mt-0.5 shrink-0" /><span>{error}</span>
                </div>
              )}

              <form onSubmit={submit} className="mt-6 space-y-4" data-testid="invite-accept-form">
                <div>
                  <label className="block text-xs uppercase tracking-[0.18em] font-bold text-[#0A0A0A] mb-2">Your full name</label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    data-testid="invite-name-input"
                    className="w-full px-4 py-3.5 bg-white border border-[#0A0A0A]/15 focus:outline-none focus:border-[#1E4D55] focus:ring-2 focus:ring-[#1E4D55]/15 text-sm"
                    autoFocus
                  />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-[0.18em] font-bold text-[#0A0A0A] mb-2">Mobile (optional)</label>
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="+44 7700 900123"
                    data-testid="invite-phone-input"
                    className="w-full px-4 py-3.5 bg-white border border-[#0A0A0A]/15 focus:outline-none focus:border-[#1E4D55] focus:ring-2 focus:ring-[#1E4D55]/15 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-[0.18em] font-bold text-[#0A0A0A] mb-2">Choose a password</label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    data-testid="invite-password-input"
                    className="w-full px-4 py-3.5 bg-white border border-[#0A0A0A]/15 focus:outline-none focus:border-[#1E4D55] focus:ring-2 focus:ring-[#1E4D55]/15 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-[0.18em] font-bold text-[#0A0A0A] mb-2">Confirm password</label>
                  <input
                    type="password"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    data-testid="invite-password-confirm-input"
                    className="w-full px-4 py-3.5 bg-white border border-[#0A0A0A]/15 focus:outline-none focus:border-[#1E4D55] focus:ring-2 focus:ring-[#1E4D55]/15 text-sm"
                  />
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="invite-accept-submit"
                  className="w-full inline-flex items-center justify-center gap-2 bg-[#1E4D55] hover:bg-[#163C42] text-white py-3.5 font-semibold text-sm disabled:opacity-60"
                >
                  {submitting ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={16} />}
                  Activate my account
                </button>
              </form>
            </>
          )}
        </div>
      </motion.div>
    </div>
  );
}
